import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { startOfDay } from "date-fns";
import { getBrowserTimezone } from "app/constants/timezone";

const DATE_STORAGE_KEY = "sbch_global_date_range";
const TIMEZONE_STORAGE_KEY = "sbch_global_timezone";

// Default view is "today" - midnight up to right now, not through the end of
// a day that hasn't happened yet.
const getDefaultDateRange = () => ({
  startDate: startOfDay(new Date()),
  endDate: new Date(),
});

// The date/timezone filter is picked once (usually on the Dashboard) and is
// expected to stay in effect across client-side navigation as well as a hard
// refresh, so it's mirrored into localStorage alongside the in-memory state.
// The range is stored as instants, which only mean "today" in the zone they
// were picked in - after a laptop timezone change they'd straddle two days
// (and headers would switch from "Today, hh:mm" to a date range), so a range
// saved under a different browser zone falls back to today in the new zone.
const readStoredDateRange = () => {
  try {
    const raw = localStorage.getItem(DATE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.browserTz !== getBrowserTimezone()) {
      localStorage.removeItem(DATE_STORAGE_KEY);
      return null;
    }
    const startDate = new Date(parsed.startDate);
    const endDate = new Date(parsed.endDate);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
    return { startDate, endDate };
  } catch (e) {
    return null;
  }
};

// The browser's own zone is the default. A different zone the user picks is
// stored together with the browser zone it was picked under, and dropped once
// the laptop's timezone changes, so the app follows the new local zone again.
// Older plain-string values have no browser zone recorded, so they're ignored.
const readStoredTimezone = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(TIMEZONE_STORAGE_KEY));
    if (parsed?.tz && parsed.browserTz === getBrowserTimezone()) {
      return parsed.tz;
    }
    localStorage.removeItem(TIMEZONE_STORAGE_KEY);
    return null;
  } catch (e) {
    return null;
  }
};

const DateContext = createContext({
  startDate: startOfDay(new Date()),
  endDate: new Date(),
});

function DateProvider({ children }) {
  const [globalDate, setGlobalDateState] = useState(
    () => readStoredDateRange() || getDefaultDateRange()
  );
  const [globalTimezone, setGlobalTimezoneState] = useState(
    () => readStoredTimezone() || getBrowserTimezone()
  );
  const browserTimezoneRef = useRef(getBrowserTimezone());

  // Pick up a laptop timezone change without a reload: re-check whenever the
  // tab comes back into focus.
  useEffect(() => {
    const syncWithBrowserTimezone = () => {
      const current = getBrowserTimezone();
      if (current === browserTimezoneRef.current) return;
      browserTimezoneRef.current = current;
      setGlobalTimezoneState(readStoredTimezone() || current);
      setGlobalDateState(readStoredDateRange() || getDefaultDateRange());
    };
    window.addEventListener("focus", syncWithBrowserTimezone);
    document.addEventListener("visibilitychange", syncWithBrowserTimezone);
    return () => {
      window.removeEventListener("focus", syncWithBrowserTimezone);
      document.removeEventListener("visibilitychange", syncWithBrowserTimezone);
    };
  }, []);

  const setGlobalDate = (range) => {
    setGlobalDateState(range);
    try {
      localStorage.setItem(
        DATE_STORAGE_KEY,
        JSON.stringify({ ...range, browserTz: getBrowserTimezone() })
      );
    } catch (e) {
      // Ignore storage errors (e.g. private browsing with storage disabled).
    }
  };

  const setGlobalTimezone = (tz) => {
    setGlobalTimezoneState(tz);
    try {
      const browserTz = getBrowserTimezone();
      // Picking the browser's own zone means "follow the laptop", so nothing
      // is stored and a later laptop timezone change still applies.
      if (!tz || tz === browserTz) {
        localStorage.removeItem(TIMEZONE_STORAGE_KEY);
      } else {
        localStorage.setItem(
          TIMEZONE_STORAGE_KEY,
          JSON.stringify({ tz, browserTz })
        );
      }
    } catch (e) {
      // Ignore storage errors (e.g. private browsing with storage disabled).
    }
  };

  return (
    <DateContext.Provider
      value={{ globalDate, setGlobalDate, globalTimezone, setGlobalTimezone }}
    >
      {children}
    </DateContext.Provider>
  );
}

const useDate = () => useContext(DateContext);

export { useDate, DateProvider };
